import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { NativeSelect } from '@/components/ui/Input';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { ApiError } from '@/lib/api/client';
import { keys, tenantKey } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import {
  fetchActivitySummary, fetchTasks, fetchTask, retryTask, cancelTask, taskState,
  type AgentSummary, type AiTask, type TaskFilter,
} from './api';
import { CopilotGapsSection } from './CopilotGapsSection';

/**
 * Admin → AI → AI Activity (Phase 1 quotation G3, gap analysis §4.3a). The one
 * place the RFQ's "agents" are named. Staff never see agents: they see the
 * state of their own work where it happens, and a bell item if it failed.
 */
export default function AIActivityTab() {
  const [window, setWindow] = useState('24h');
  const [agent, setAgent] = useState('');
  const [status, setStatus] = useState<TaskFilter>('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [showHow, setShowHow] = useState(false);

  const summary = useQuery({
    queryKey: keys.admin.aiActivitySummary(window),
    queryFn: () => fetchActivitySummary(window),
    refetchInterval: 30_000,
  });
  const tasks = useQuery({
    queryKey: keys.admin.aiActivityTasks({ agent, status, page }),
    queryFn: () => fetchTasks({ agent, status, page }),
    refetchInterval: 15_000,
  });

  const filterTo = (agentId: string, st: TaskFilter = '') => { setAgent(agentId); setStatus(st); setPage(1); };

  if (summary.isLoading) return <LoadingState />;
  if (summary.isError || !summary.data) return <ErrorState message="Couldn’t load AI activity." onRetry={() => summary.refetch()} />;
  const s = summary.data;

  const columns: DataTableColumn<AiTask>[] = [
    { key: 'when', header: 'Started', widthClassName: 'w-[130px]', className: 'text-ink-muted',
      render: (t) => fmtTime(t.started_at || t.created_at) },
    { key: 'agent', header: 'Agent', widthClassName: 'w-[170px]', render: (t) => agentLabel(s.agents, t.agent) },
    { key: 'task', header: 'Task', render: (t) => (
      <span className="flex flex-col">
        <span>{t.kind_label}</span>
        <span className="text-[11.5px] text-ink-muted">{[t.uhid, t.branch_name, t.created_by].filter(Boolean).join(' · ')}</span>
      </span>
    ) },
    { key: 'status', header: 'Status', widthClassName: 'w-[150px]', render: (t) => {
      const st = taskState(t);
      return <Badge variant={st.tone}>{st.label}</Badge>;
    } },
    { key: 'tries', header: 'Tries', widthClassName: 'w-[70px]', align: 'center', render: (t) => `${t.attempts}/${t.max_attempts}` },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <QueueLine s={s} onNeedsPerson={() => filterTo('', 'needs_person')} />
        <div className="ml-auto flex items-center gap-2 text-xs text-ink-muted">
          Show
          <NativeSelect value={window} onChange={(e) => setWindow(e.target.value)} className="w-auto text-xs" aria-label="Time window">
            <option value="1h">Last hour</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </NativeSelect>
        </div>
      </div>

      {!s.ai_enabled && (
        <div className="rounded-(--radius) bg-warn-soft px-3 py-2 text-xs text-warn-ink">
          AI is switched off for this hospital (Admin → AI → AI Settings). Everything still works by hand; AI agents show as Off.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {s.agents.map((a) => (
          <AgentCard key={a.id} a={a} active={agent === a.id} onSelect={() => filterTo(agent === a.id ? '' : a.id)} />
        ))}
      </div>

      <button type="button" onClick={() => setShowHow((v) => !v)}
        className="flex items-center gap-1 self-start text-[13px] font-semibold text-teal hover:underline">
        {showHow ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} How AI works here
      </button>
      {showHow && <HowItWorks agents={s.agents} />}

      <CopilotGapsSection />

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[13px] font-semibold text-ink">Background tasks</div>
        <NativeSelect value={agent} onChange={(e) => filterTo(e.target.value, status)} className="w-auto text-xs" aria-label="Agent">
          <option value="">All agents</option>
          {s.agents.filter((a) => a.kind === 'ai').map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </NativeSelect>
        <NativeSelect value={status} onChange={(e) => { setStatus(e.target.value as TaskFilter); setPage(1); }} className="w-auto text-xs" aria-label="Status">
          <option value="">Any status</option>
          <option value="needs_person">Needs a decision</option>
          <option value="waiting_retry">Retrying soon</option>
          <option value="running">Running</option>
          <option value="succeeded">Done</option>
          <option value="cancelled">Cancelled</option>
        </NativeSelect>
      </div>
      <DataTable
        columns={columns}
        rows={tasks.data?.data ?? []}
        rowKey={(t) => t.id}
        onRowClick={(t) => setOpenId(t.id)}
        isLoading={tasks.isLoading}
        isError={tasks.isError}
        onRetry={() => tasks.refetch()}
        emptyLabel="No background AI tasks match."
        density="compact"
      />
      {tasks.data && (tasks.data.page > 1 || tasks.data.has_more) && (
        <div className="flex items-center justify-end gap-2 text-xs text-ink-muted">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          Page {tasks.data.page}
          <Button variant="ghost" size="sm" disabled={!tasks.data.has_more} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      <TaskSheet id={openId} agents={s.agents} onClose={() => setOpenId(null)} />
    </div>
  );
}

const HEALTH: Record<AgentSummary['health'], { label: string; tone: 'success' | 'warn' | 'neutral' | 'info' }> = {
  healthy: { label: 'Working', tone: 'success' },
  degraded: { label: 'Needs attention', tone: 'warn' },
  off: { label: 'Off', tone: 'neutral' },
  not_set_up: { label: 'Not set up', tone: 'neutral' },
};

function AgentCard({ a, active, onSelect }: { a: AgentSummary; active: boolean; onSelect: () => void }) {
  const h = HEALTH[a.health];
  const clickable = a.kind === 'ai';
  return (
    <button type="button" disabled={!clickable} onClick={onSelect}
      className={cn('flex flex-col gap-1.5 rounded-xl border bg-card p-3 text-left transition-colors',
        active ? 'border-teal ring-1 ring-teal' : 'border-border', clickable ? 'hover:border-teal' : 'cursor-default')}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[13px] font-semibold text-ink">{a.label}</span>
        <Badge variant={h.tone} className="shrink-0 whitespace-nowrap">{h.label}</Badge>
      </div>
      {a.kind === 'rules' && (
        <div className="text-[12px] text-ink-muted">
          Runs on rules, not AI.{a.activity ? ` ${a.activity.count} ${a.activity.label}.` : ''}
        </div>
      )}
      {a.kind === 'planned' && <div className="text-[12px] text-ink-muted">Not built yet.</div>}
      {a.kind === 'ai' && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[12px] text-ink-muted">
          <span>AI calls <b className="text-ink">{a.calls}</b>{a.call_errors ? <span className="text-danger-ink"> · {a.call_errors} failed</span> : null}</span>
          <span>Tasks <b className="text-ink">{a.tasks}</b>{a.task_failures ? <span className="text-danger-ink"> · {a.task_failures} failed</span> : null}</span>
          <span>Avg {a.avg_latency_ms != null ? `${(a.avg_latency_ms / 1000).toFixed(1)}s` : '—'}</span>
          <span>Cost {a.cost ? `${a.currency === 'INR' ? '₹' : '$'}${a.cost.toFixed(2)}` : '—'}</span>
          {a.needs_person > 0 && <span className="col-span-2 font-semibold text-warn-ink">{a.needs_person} need a decision</span>}
        </div>
      )}
    </button>
  );
}

function QueueLine({ s, onNeedsPerson }: { s: { queue: { running: number; pending: number; waiting_retry: number; needs_person: number }; providers: { provider: string; calls: number; errors: number }[] }; onNeedsPerson: () => void }) {
  const q = s.queue;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-ink-muted">
      <span>Running <b className="text-ink">{q.running + q.pending}</b></span>
      <span>Retrying soon <b className="text-ink">{q.waiting_retry}</b></span>
      {q.needs_person > 0
        ? <Button variant="link" className="text-[12.5px] text-warn-ink" onClick={onNeedsPerson}>{q.needs_person} need a decision</Button>
        : <span>Needs a decision <b className="text-ink">0</b></span>}
      {s.providers.map((p) => (
        <span key={p.provider} className="flex items-center gap-1">
          <span className={cn('inline-block h-2 w-2 rounded-full', p.errors && p.errors * 2 >= p.calls ? 'bg-danger' : 'bg-success')} />
          {p.provider} {p.errors ? `(${p.errors}/${p.calls} failed in the last hour)` : ''}
        </span>
      ))}
    </div>
  );
}

function HowItWorks({ agents }: { agents: AgentSummary[] }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 text-[13px]">
      <p className="mb-3 text-ink">
        AI prepares; people decide. Every AI result is a draft or a suggestion that a member of staff checks. Bills,
        GST, balances and stock are always calculated by the system’s own rules, never by AI. If AI is unavailable,
        every screen keeps working by hand.
      </p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {agents.map((a) => (
          <li key={a.id}>
            <div className="font-semibold text-ink">{a.label}{a.kind === 'rules' ? ' (rules, not AI)' : a.kind === 'planned' ? ' (not built yet)' : ''}</div>
            <div className="text-ink-muted">{a.does}</div>
            <div className="text-ink-muted"><span className="font-semibold">Human control:</span> {a.human}</div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-ink-muted">
        Background work (reading documents and supplier invoices) is retried automatically after 1, 5 and 15 minutes.
        If it still fails, it waits here for someone to retry it or cancel it and enter the details by hand.
      </p>
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  started: 'Started', succeeded: 'Done', failed: 'Failed', automatic_retry: 'Tried again automatically',
  retry_requested: 'Retried by', cancelled: 'Cancelled by', interrupted: 'Interrupted by a server restart',
};

function TaskSheet({ id, agents, onClose }: { id: number | null; agents: AgentSummary[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Keep showing the last task while the sheet animates closed (id is already null then).
  const lastId = useRef<number | null>(null);
  if (id != null) lastId.current = id;
  const shownId = lastId.current ?? 0;
  const detail = useQuery({ queryKey: keys.admin.aiActivityTask(shownId), queryFn: () => fetchTask(shownId), enabled: id != null, refetchInterval: 5_000 });
  const refresh = () => qc.invalidateQueries({ queryKey: tenantKey('admin', 'ai-activity') });
  const errText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong');
  const retry = useMutation({
    mutationFn: () => retryTask(shownId),
    onSuccess: () => { toast({ title: 'Retrying now', variant: 'success' }); refresh(); },
    onError: (e) => toast({ title: 'Couldn’t retry', description: errText(e), variant: 'danger' }),
  });
  const cancel = useMutation({
    mutationFn: () => cancelTask(shownId),
    onSuccess: () => { setConfirmCancel(false); toast({ title: 'Task cancelled', variant: 'success' }); refresh(); },
    onError: (e) => toast({ title: 'Couldn’t cancel', description: errText(e), variant: 'danger' }),
  });
  const t = detail.data;
  const st = t ? taskState(t) : null;
  const canRetry = !!t && t.status === 'failed' && t.retry_safe;
  const canCancel = !!t && (t.status === 'failed' || t.status === 'pending');

  return (
    <Sheet open={id != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t?.kind_label ?? 'AI task'}</SheetTitle>
          <SheetDescription>{t ? `${agentLabel(agents, t.agent)} · task #${t.id}` : ''}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {!t ? <LoadingState /> : (
            <div className="flex flex-col gap-4 text-[13px]">
              <div className="flex items-center gap-2">{st && <Badge variant={st.tone}>{st.label}</Badge>}
                <span className="text-ink-muted">Attempt {t.attempts} of {t.max_attempts}</span></div>
              {t.last_error && t.status !== 'succeeded' && (
                <div className="rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">{t.last_error}</div>
              )}
              {t.status === 'failed' && t.next_attempt_at && (
                <div className="text-ink-muted">Next automatic try: {fmtTime(t.next_attempt_at)}</div>
              )}
              <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1">
                {t.patient_name && <><dt className="text-ink-muted">Patient</dt><dd>{t.patient_name} ({t.uhid})</dd></>}
                <dt className="text-ink-muted">Record</dt><dd>{t.subject_type.replace(/_/g, ' ')} #{t.subject_id}</dd>
                {t.branch_name && <><dt className="text-ink-muted">Branch</dt><dd>{t.branch_name}</dd></>}
                <dt className="text-ink-muted">Started by</dt><dd>{t.created_by || '—'}</dd>
                {t.result_summary && Object.keys(t.result_summary).length > 0 && (
                  <><dt className="text-ink-muted">Result</dt><dd>{Object.entries(t.result_summary).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v)}`).join(', ')}</dd></>
                )}
              </dl>
              <div>
                <div className="mb-1 font-semibold text-ink">History</div>
                <ol className="border-l border-border pl-3">
                  {t.history.map((h, i) => (
                    <li key={i} className="mb-1.5">
                      <div className="text-ink">{EVENT_LABEL[h.event] || h.event}{h.by ? ` ${h.by}` : ''}
                        {h.retry_in_minutes ? <span className="text-ink-muted"> — next try in {h.retry_in_minutes} min</span> : null}</div>
                      <div className="text-[11.5px] text-ink-muted">{fmtTime(h.at)}{h.error ? ` · ${h.error}` : ''}</div>
                    </li>
                  ))}
                </ol>
              </div>
              {!t.retry_safe && t.status === 'failed' && (
                <div className="text-ink-muted">This kind of work isn’t repeated automatically. Cancel it and complete the step by hand.</div>
              )}
            </div>
          )}
        </SheetBody>
        {(canRetry || canCancel) && (
          <SheetFooter>
            {canCancel && <Button variant="ghost" onClick={() => setConfirmCancel(true)}>Cancel task</Button>}
            {canRetry && <Button onClick={() => retry.mutate()} loading={retry.isPending}>Retry now</Button>}
          </SheetFooter>
        )}
        <ConfirmDialog
          open={confirmCancel}
          onOpenChange={setConfirmCancel}
          title="Cancel this task?"
          description="It won’t be tried again. The document or invoice stays marked as not read, and staff enter the details by hand."
          confirmLabel="Cancel task"
          onConfirm={() => cancel.mutate()}
        />
      </SheetContent>
    </Sheet>
  );
}

function agentLabel(agents: AgentSummary[], id: string) {
  return agents.find((a) => a.id === id)?.label ?? id;
}

function fmtTime(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
